import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Calendar, Building2, School } from 'lucide-react';
import { prestationsOrganisme, formationsInternes } from '../data/mockData';

const Home = () => {
  return (
    <div>
      {/* Hero Section */}
      <div 
        className="relative h-[500px] flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1640145867966-be8e200a7891?w=1920&h=500&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/60 to-black/70"></div>
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="mb-4 text-sm">
            <span className="text-gray-300">Accueil</span>
            <span className="mx-2 text-red-500">/</span>
            <span className="text-red-500">Formations</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 uppercase tracking-wide">
            Formations professionnelles en sécurité
          </h1>
          
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Organisme de formation spécialisé en sécurité incendie, secourisme et sécurité privée. 
            Formations sur mesure et sessions inter-entreprises.
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Link to="/prestations" className="flex flex-col items-center p-6 bg-red-50 rounded-lg hover:bg-red-100 transition-colors group">
              <Building2 size={48} className="text-red-600 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Prestations en organisme</h3>
              <p className="text-gray-600 text-center text-sm">Nous intervenons dans votre structure</p>
            </Link>
            
            <Link to="/formations" className="flex flex-col items-center p-6 bg-red-50 rounded-lg hover:bg-red-100 transition-colors group">
              <School size={48} className="text-red-600 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Formations internes</h3>
              <p className="text-gray-600 text-center text-sm">Sessions organisées dans nos centres</p>
            </Link>
            
            <Link to="/calendrier" className="flex flex-col items-center p-6 bg-red-50 rounded-lg hover:bg-red-100 transition-colors group">
              <Calendar size={48} className="text-red-600 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Calendrier des sessions</h3>
              <p className="text-gray-600 text-center text-sm">Consultez nos prochaines dates</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Prestations Overview */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Prestations en organisme
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Nous intervenons directement dans votre établissement avec du matériel professionnel
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {prestationsOrganisme.slice(0, 6).map((category) => (
              <Link
                key={category.id}
                to={`/prestations/${category.slug}`}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all p-6 border-l-4 border-red-600"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">{category.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{category.description}</p>
                <p className="text-red-600 font-semibold">{category.formationsCount} formation{category.formationsCount > 1 ? 's' : ''}</p>
              </Link>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/prestations" className="inline-block bg-red-600 text-white px-8 py-3 rounded-md hover:bg-red-700 transition-colors font-semibold">
              VOIR TOUTES LES PRESTATIONS
            </Link>
          </div>
        </div>
      </section>

      {/* Formations Internes Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Formations internes
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Sessions inter-entreprises dans nos centres de formation certifiés
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {formationsInternes.map((category) => (
              <Link
                key={category.id}
                to={`/formations/${category.slug}`}
                className="bg-gray-50 rounded-lg shadow-md hover:shadow-xl transition-all p-6 border-t-4 border-red-600"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">{category.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{category.description}</p>
                <p className="text-red-600 font-semibold">{category.formationsCount} formation{category.formationsCount > 1 ? 's' : ''}</p>
              </Link>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/formations" className="inline-block bg-red-600 text-white px-8 py-3 rounded-md hover:bg-red-700 transition-colors font-semibold">
              VOIR TOUTES LES FORMATIONS
            </Link>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
            Organisme de formation certifié
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Formation du Dragon est un organisme certifié Qualiopi et agréé pour les formations SSIAP
          </p>
          
          <div className="flex flex-wrap justify-center items-center gap-12">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center justify-center w-32 h-32 bg-white rounded-lg hover:shadow-lg transition-shadow">
                <Award size={48} className="text-red-600" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une formation personnalisée ?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour discuter de vos besoins spécifiques. 
            Nous créons des formations sur mesure adaptées à votre activité.
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-white text-red-600 px-8 py-4 rounded-md hover:bg-gray-100 transition-colors font-bold text-lg"
          >
            DEMANDEZ UN DEVIS GRATUIT
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;